import { ContentLink } from "@robe/client/xcore-ui";
import { FileValue, Localized } from "@robe/core";
import { Button, ExtendedButtonProps, ExtendedLinkProps, ExtendedTextProps, IconProps, Link, Modal, Text, useModal } from "@xcorejs/ui";
import ExternalSVG from "components/ExternalSVG";
import { PropsWithChildren } from "react";
import styled from "styled-components";
import { useLayout } from "xcore";
import { SmartLink as SmartLinkType } from "xcore/types";

export interface SmartLinkProps {
  _link?: Omit<ExtendedLinkProps, "as" | "content">;
  _text?: ExtendedTextProps;
  _iconProps?: IconProps;
  asButton?: boolean;
  _buttonProps?: ExtendedButtonProps;
  withoutHighlight?: boolean; // If true, do not show red line on hover
  collapsableSubheaderItemLink?: boolean; // Je mi to úplně jedno i kdyby tady těchto props bylo 100, nemám na to nervy
  smartLink: Pick<SmartLinkType, "values">;
}

/**
 * Link component that will render type of link defined in `smartLink` content type content.
 * There are 3 types of smart links that are render based on this priority:
 * 1. Internal link => Link to internal content.
 * 2. External link => Link to external URL.
 * 3. Modal link => Component that opens modal on click with custom HTML or iframe.
 */
const SmartLink = (props: PropsWithChildren<SmartLinkProps>) => {
  const {
    smartLink: { values },
    _text,
    _iconProps,
    asButton,
    _buttonProps,
  } = props;
  const { localize } = useLayout();

  const relationLinkResolved = values?.relation ? localize(values?.relation) : null;
  const externalUrlLinkResolved = values?.externalUrl ? localize(values?.externalUrl) : null;
  const embeddedModalLinkResolved = values?.embedded ? localize(values?.embedded) : null;
  const hasNoLinks = !relationLinkResolved && !embeddedModalLinkResolved && !externalUrlLinkResolved;

  if (!values) {
    return null;
  }

  if (hasNoLinks) {
    return <Text {..._text}>Invalid link</Text>;
  }

  const type = relationLinkResolved ? "relation" : externalUrlLinkResolved ? "url" : "embeded";

  if (props?.collapsableSubheaderItemLink) {
    return (
      <CollapsableSubheaderItemLink>
        <UrlLink {...props}>
          <SmartLinkText
            icon={values.icon}
            name={values.name}
            withoutHighlight={props.withoutHighlight}
            {...{
              asButton,
              _buttonProps,
              _iconProps,
              _text,
              children: props?.children,
            }}
          />
        </UrlLink>
      </CollapsableSubheaderItemLink>
    );
  }

  return (
    <>
      {type === "relation" ? (
        <RelationLink {...props}>
          <SmartLinkText
            icon={values.icon}
            name={values.name}
            {...{
              asButton,
              _buttonProps,
              _iconProps,
              _text,
              children: props?.children,
            }}
          />
        </RelationLink>
      ) : type === "url" ? (
        <UrlLink {...props}>
          <SmartLinkText
            icon={values.icon}
            name={values.name}
            withoutHighlight={props.withoutHighlight}
            {...{
              asButton,
              _buttonProps,
              _iconProps,
              _text,
              children: props?.children,
            }}
          />
        </UrlLink>
      ) : (
        <EmbeddedLink {...props}>
          <SmartLinkText
            icon={values.icon}
            name={values.name}
            {...{
              asButton,
              _buttonProps,
              _iconProps,
              _text,
              children: props?.children,
            }}
          />
        </EmbeddedLink>
      )}
    </>
  );
};

export default SmartLink;

interface SmartLinkTextProps extends Omit<SmartLinkProps, "_link" | "smartLink"> {
  icon: FileValue | undefined;
  name: Localized;
}

const SmartLinkText = ({
  withoutHighlight,
  asButton,
  icon,
  name,
  _iconProps,
  _buttonProps,
  _text,
  children,
}: PropsWithChildren<SmartLinkTextProps>) => {
  const { stringify, image } = useLayout();
  const iconSrc = icon ? image(icon) : null;

  // This needs to be ExternalSVG because in some places fill color changes on hover
  if (asButton) {
    return (
      <Button
        leftIcon={
          iconSrc && (
            <ExternalSVG
              src={iconSrc}
              mr="1rem"
              fill="#fff"
              display="flex"
              width="1.6rem"
              minWidth="1.6rem"
              height="1.6rem"
              _group={{ _hover: { _icon: { fill: "#fff" } } }}
              {..._iconProps}
            />
          )
        }
        {..._buttonProps}
      >
        {stringify(name)}
      </Button>
    );
  }

  return (
    <>
      {iconSrc && (
        <ExternalSVG
          src={iconSrc}
          fill="gray"
          display="flex"
          width="1.6rem"
          minWidth="1.6rem"
          height="1.6rem"
          _group={{ _hover: { _icon: { fill: "#fff" } } }}
          {..._iconProps}
        />
      )}
      {withoutHighlight ? (
        <TextWithoutHiglight>{children ?? stringify(name)}</TextWithoutHiglight>
      ) : (
        <Text {..._text}>{children ?? stringify(name)}</Text>
      )}
    </>
  );
};

const RelationLink = ({ smartLink: { values }, _link, children }: PropsWithChildren<SmartLinkProps>) => {
  const { localize } = useLayout();
  const content = localize(values.relation);

  if (!content || !content.type || !content.slug) {
    return null;
  }

  return (
    <ContentLink
      type="simple"
      content={{ id: content.id, type: content.type, slug: content.slug }}
      {...(localize(values.newWindow) && { target: "_blank" })}
      role="group"
      {..._link}
    >
      {children}
    </ContentLink>
  );
};

const UrlLink = ({ smartLink: { values }, _link, children }: PropsWithChildren<SmartLinkProps>) => {
  const { stringify, localize } = useLayout();

  return (
    <Link
      href={stringify(values.externalUrl)}
      {...(localize(values.newWindow) && { target: "_blank" })}
      type="simple"
      role="group"
      {..._link}
    >
      {children}
    </Link>
  );
};

const EmbeddedLink = ({ smartLink: { values }, _link, children }: PropsWithChildren<SmartLinkProps>) => {
  const { stringify } = useLayout();
  const [open] = useModal(EmbeddedModal, { html: stringify(values.embedded) });

  return (
    <Link as="span" type="simple" onClick={open} role="group" {..._link}>
      {children}
    </Link>
  );
};

interface EmbeddedModalProps {
  html: string;
}

const EmbeddedModal = ({ html }: EmbeddedModalProps) => {
  return (
    <Modal
      size="lg"
      _overlay={{
        zIndex: 5,
      }}
      px="0 !important"
      _close={{ zIndex: 5 }}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Modal>
  );
};

const CollapsableSubheaderItemLink = styled("span")`
  width: 100%;

  &:hover * {
    background: white !important;
    color: #1e1e1e !important;
  }
`;

const TextWithoutHiglight = styled.span`
  display: flex;
  align-items: center;
  font-family: avenir;
  font-size: 1.2rem;
  padding: 0 1rem;
  height: 4.6rem;
  font-weight: 500;
  line-height: 2.1rem;
  color: lightgray !important;
  background: none !important;
  transition:
    color 300ms,
    background 300ms;
  text-align: center;

  @media (min-width: 768px) {
    padding: 0 1.2rem;
  }
  @media (min-width: 1024px) {
    font-size: 1.4rem;
  }
  @media (min-width: 1280px) {
    padding: 0 2rem;
  }

  &:hover {
    background: white !important;
    color: #1e1e1e !important;
    z-index: 10;
  }
`;
